import type { BurningDeckState, GameState, PlayerState } from "../types/game";
import { normalizeHouseRules, STREAK_TURNS_TO_IGNITE, streaksApply, type HouseRules } from "./houseRules";

/**
 * Burning Deck: a deck that stays alight keeps burning. Five clean turns light
 * it (Jourdan's Streaks), and on every other turn from the seventh its top card
 * burns away as the turn begins. The card leaves play entirely: it lands on no
 * pile, fires nothing if it is a wild, charges no star and wins no bounty. It is
 * simply gone from the deck.
 *
 * Judged in `endTurn` (moves.ts), after the turn has passed to the next player
 * and their streak locks are chosen, off the streak the player carries INTO the
 * turn. Spending the streak or taking a pile zeroes it, so either restarts the
 * cycle with no counting of its own.
 *
 * CPU seats spend a streak on sight (`decideStreakTarget`), so they rarely hold
 * one long enough to burn; the rule is for the human who saves it.
 *
 * Mirrors services/game-server/internal/engine/burningdeck.go.
 */

/** Streak turns past ignition before the first burn: ignition + 2 is the seventh. */
export const BURNING_DECK_FIRST_BURN_AFTER = 2;
/** After the first burn, a card burns every this many further turns alight. */
export const BURNING_DECK_BURN_EVERY = 2;
/**
 * The fewest cards a deck must hold to lose one. The last card is never burned:
 * going out is a play (Vinny's Rule exists to keep it one), so a burn can never
 * put a player out of the round.
 */
export const BURNING_DECK_MIN_CARDS = 2;

/** Whether Burning Deck is in force at this table: the flag, plus a table Streaks works at. */
export function burningDeckApplies(rules: HouseRules, playerCount: number): boolean {
  return rules.burningDeck && streaksApply(rules, playerCount);
}

/**
 * The table's Burning Deck state when the rule is in force, else null. Absent
 * state reads as OFF, as Mercy's and Drought's do, so hand-built states (engine
 * tests, the tutorial, staged boards) never burn a card without asking.
 */
export function activeBurningDeck(state: GameState): BurningDeckState | null {
  const b = state.burningDeck;
  if (!b || !burningDeckApplies(normalizeHouseRules(state.houseRules), state.players.length)) return null;
  return b;
}

/** Whether a deck arriving at its turn with this streak burns a card: 7, 9, 11... */
export function isBurnStreak(streak: number | undefined): boolean {
  const past = (streak ?? 0) - STREAK_TURNS_TO_IGNITE - BURNING_DECK_FIRST_BURN_AFTER;
  return past >= 0 && past % BURNING_DECK_BURN_EVERY === 0;
}

/** Whether this player's deck is one a burn can take a card from. */
export function canBurnFrom(player: PlayerState): boolean {
  return player.deck.length >= BURNING_DECK_MIN_CARDS;
}

/** A fresh state for a round being dealt. */
export function newBurningDeckState(): BurningDeckState {
  return { burned: {} };
}
